import React, { useEffect, useMemo, useRef, useState } from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import type { CollapseProps } from 'antd';
import { Button, Collapse, Input } from 'antd';
import { ContactsOutlined, HistoryOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import AppPageHeader from './components/AppPageHeader';
import Updates from './Updates';
import SagaRaven, { SAGA_SINGLETON_PRIORITIES } from './SagaRaven';
import axios from 'axios';
import { HUMAN_REVIEW_PROVIDERS_ROUTE } from './serviceProviderDirectory';
import { LANGUAGE_CONFIG } from './LanguageConfig';
import './About.css';

/** Types */
type FAQItem = {
  key: string;
  question: string;
  answer: React.ReactNode;
  plainText: string;
  category: FAQCategoryKey;
};

type TabKey = 'faq' | 'updates' | 'contact';
type FAQCategoryKey = 'all' | 'credits' | 'rights' | 'languages' | 'formatting' | 'publishing';
type AboutTab = {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
};

type FAQCategoryFilter = {
  key: FAQCategoryKey;
  label: string;
};

const panelStyle: React.CSSProperties = {
  borderRadius: 8,
  marginBottom: 10,
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-base)',
  overflow: 'hidden',
};

const API_BASE = resolveFrontendApiBase();
const WORDS_COUNTER_ENDPOINT = `${API_BASE}/public/word-count`;
const WORDS_COUNTER_CACHE_TTL_MS = 5 * 60 * 1000;
const DEFAULT_WORDS_PER_SECOND = 12;
const MIN_WORDS_PER_SECOND = 2;

const faqCategoryFilters: FAQCategoryFilter[] = [
  { key: 'all', label: 'All' },
  { key: 'credits', label: 'Credits' },
  { key: 'rights', label: 'Rights & privacy' },
  { key: 'languages', label: 'Languages' },
  { key: 'formatting', label: 'Formatting' },
  { key: 'publishing', label: 'Publishing' },
];

const faqCategoryKeys = new Set<FAQCategoryKey>(
  faqCategoryFilters.map((filter) => filter.key)
);

const parseTabParam = (value: string | null): TabKey => (
  value === 'updates' || value === 'contact' ? value : 'faq'
);

const parseTopicParam = (value: string | null): FAQCategoryKey => (
  value && faqCategoryKeys.has(value as FAQCategoryKey) ? value as FAQCategoryKey : 'all'
);

type WordsCounterCache = {
  value: number;
  fetchedAt: number;
};

type WordsCounterResponse = {
  total_words_translated?: number;
  refreshed_at?: string;
};

let wordsCounterCache: WordsCounterCache | null = null;
let wordsCounterInflight: Promise<number> | null = null;

const getCachedWordsTranslated = (): number | null => {
  if (!wordsCounterCache) return null;
  if (Date.now() - wordsCounterCache.fetchedAt > WORDS_COUNTER_CACHE_TTL_MS) {
    return null;
  }
  return wordsCounterCache.value;
};

const normalizeWordsTranslated = (value: unknown): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }
  return numeric;
};

const requestWordsTranslated = async (): Promise<number> => {
  const { data } = await axios.get<WordsCounterResponse>(WORDS_COUNTER_ENDPOINT);
  return normalizeWordsTranslated(data?.total_words_translated);
};

const fetchWordsTranslated = async (): Promise<number> => {
  const cached = getCachedWordsTranslated();
  if (cached !== null) {
    return cached;
  }
  if (wordsCounterInflight) {
    return wordsCounterInflight;
  }
  wordsCounterInflight = requestWordsTranslated()
    .then((value) => {
      wordsCounterCache = { value, fetchedAt: Date.now() };
      return value;
    })
    .catch((error) => {
      throw error;
    })
    .finally(() => {
      wordsCounterInflight = null;
    });
  return wordsCounterInflight;
};

const supportedLanguagesText = LANGUAGE_CONFIG.baseLanguages.join(', ');
const betaLanguagesText = LANGUAGE_CONFIG.betaLanguages.join(', ');

/** Data rebuilt from existing About FAQ so we can enable search */
const faqItems: FAQItem[] = [
  { key: '1', category: 'publishing', question: 'How Does AI Translation Work?', plainText: 'AI translation uses machine learning trained on large datasets to translate novels accurately and fluently.', answer: (<p>AI translation employs sophisticated machine learning algorithms trained on extensive text datasets to provide accurate and fluent translations of novels.</p>) },
  { key: '2', category: 'rights', question: 'Do I Retain the Rights to AI Translations of My Work?', plainText: 'You retain all rights to your original work and its AI translations. We do not claim rights.', answer: (<p>Yes, you retain all rights to both your original work and its AI-generated translations. ScribeShadow does not claim any rights to your content.</p>) },
  {
    key: '3',
    category: 'languages',
    question: 'What Languages are Supported?',
    plainText: `Translate to and from: ${supportedLanguagesText}.${betaLanguagesText ? ` Beta languages: ${betaLanguagesText}.` : ''}`,
    answer: (
      <p>
        ScribeShadow can translate to and from: {supportedLanguagesText}.
        {betaLanguagesText && (
          <> Additional beta languages are available through our <a href="/betaaccess">Beta Program</a>: {betaLanguagesText}.</>
        )}
      </p>
    ),
  },
  { key: '4', category: 'publishing', question: 'Is There a Limit to the Length of Novels That Can Be Translated?', plainText: 'No strict length limit for translation.', answer: (<p>There is no strict limit to the length of novels that can be translated by our AI.</p>) },
  { key: '5', category: 'credits', question: 'How Much Does ShadowScribe AI Translation Cost?', plainText: 'See account page for pricing. Usually over 90 percent less than professional human services.', answer: (<p>For detailed pricing, visit our <a href="/account">account page</a>. ShadowScribe AI Translation is typically over 90% less expensive than professional human services.</p>) },
  { key: '6', category: 'languages', question: 'How Does AI Handle Cultural Nuances in Translation?', plainText: 'AI finds equivalent phrases in the target language to convey culture-specific meaning.', answer: (<p>AI translation finds equivalent phrases or expressions in the target language to convey culturally-specific meanings accurately, preserving the intended impact.</p>) },
  {
    key: '7',
    category: 'publishing',
    question: 'Is Editing Required for AI-Translated Text?',
    plainText: 'Not always. Authors make their own editing, review, and publication decisions. Some authors choose an independent human reviewer, proofreader, editor, or translation-review provider before publishing.',
    answer: (
      <>
        <p>
          Not always. Many authors use ScribeShadow as part of their translation workflow and make their own
          decisions about editing, review, and publication.
        </p>
        <p>
          Some authors choose to hire an independent human reviewer, proofreader, editor, or translation-review
          provider before publishing. This can be helpful for authors who want an additional quality check,
          need help with target-language readability, or want extra confidence before release.
        </p>
        <p>
          ScribeShadow maintains a directory of independent third-party providers who may offer these services.
          These providers are separate businesses and are responsible for their own pricing, timelines, services,
          communications, and customer support.
        </p>
        <Button type="link" className="about-inline-link-button">
          <Link to={HUMAN_REVIEW_PROVIDERS_ROUTE}>View Human Review Providers</Link>
        </Button>
      </>
    ),
  },
  { key: '8', category: 'credits', question: 'What are Credits?', plainText: 'Each translated word uses one credit.', answer: (<p>Each translated word uses one credit. Credits match the number of words you translate.</p>) },
  { key: '9', category: 'credits', question: 'What are Forever Credits?', plainText: 'Credits purchased outside your monthly plan never expire. Subscription credits stay protected while subscribed.', answer: (<p>Credits purchased outside your monthly plan are "Forever Credits" and never expire. Subscription credits also do not expire while you stay subscribed.</p>) },
  { key: '10', category: 'credits', question: 'Do Subscription Credits Expire?', plainText: 'Unused subscription credits remain available while subscribed and expire 60 days after cancellation.', answer: (<p>Unused subscription credits remain available while you stay subscribed. If your subscription is cancelled and access ends, remaining subscription credits stay usable for 60 days, then expire.</p>) },
  { key: '11', category: 'credits', question: 'Can I Try Out ScribeShadow?', plainText: 'New users receive free credits on signup.', answer: (<p>Yes! New users receive 5,000 free credits upon signup to test ScribeShadow.</p>) },
  { key: '12', category: 'rights', question: 'Do You Use My Books for AI Training?', plainText: 'No training on your books or translations.', answer: (<p>No. We never use your books or translations for AI training.</p>) },
  { key: '13', category: 'publishing', question: 'Why Opt for AI Over Human Translators?', plainText: 'AI offers speed and cost efficiency while delivering comparable quality.', answer: (<p>AI translation offers rapid turnaround and cost efficiency while delivering quality comparable to human translators.</p>) },
  {
    key: '14',
    category: 'publishing',
    question: 'Do I need human review after using ScribeShadow?',
    plainText: 'Not always. Some authors choose independent human review, proofreading, editing, or translation review before publishing. ScribeShadow maintains a directory of independent third-party providers.',
    answer: (
      <>
        <p>
          Not always. Many authors use ScribeShadow as part of their translation workflow and make their own
          decisions about editing, review, and publication.
        </p>
        <p>
          Some authors choose to hire an independent human reviewer, proofreader, editor, or translation-review
          provider before publishing. This can be helpful for authors who want an additional quality check,
          need help with target-language readability, or want extra confidence before release.
        </p>
        <p>
          ScribeShadow maintains a directory of independent third-party providers who may offer these services.
          These providers are separate businesses and are responsible for their own pricing, timelines, services,
          communications, and customer support.
        </p>
        <Button type="link" className="about-inline-link-button">
          <Link to={HUMAN_REVIEW_PROVIDERS_ROUTE}>View Human Review Providers</Link>
        </Button>
      </>
    ),
  },
  { key: '15', category: 'languages', question: 'Can the AI Translate a Novel into Multiple Languages?', plainText: 'Yes. Each target language is handled individually.', answer: (<p>Yes. We handle each target language individually to ensure accuracy and quality.</p>) },
  { key: '16', category: 'formatting', question: 'Will the Translation Preserve Formatting?', plainText: 'We strive to preserve formatting, but double check key elements.', answer: (<p>We strive to maintain all original formatting, but we recommend double-checking critical elements.</p>) },
  { key: '17', category: 'languages', question: 'Can AI Translate Sensitive Content?', plainText: 'Most content is supported, but extremely explicit material may be rejected.', answer: (<p>Our AI can handle most content, but extremely explicit material may be rejected.</p>) },
  { key: '18', category: 'credits', question: 'If Content Is Rejected, Am I Charged?', plainText: 'Rejected chapters do not incur any credits.', answer: (<p>Rejected chapters no longer deduct credits from your account.</p>) },
  { key: '19', category: 'publishing', question: 'Can I Re-Submit Rejected Content?', plainText: 'Yes. AI responses vary, and re-submission can succeed.', answer: (<p>Yes. AI responses vary, so re-submissions can succeed.</p>) },
  { key: '20', category: 'formatting', question: 'Can I Upload Word and Download ePub?', plainText: 'ePub uploads export as ePub or Word. Word uploads export as Word only.', answer: (<p>ePub uploads export as ePub or Word. Word uploads export as Word only.</p>) },
  {
    key: '21',
    category: 'languages',
    question: 'What AI Models Do You Use?',
    plainText: 'We currently use ChatGPT-5, Gemini 2.5 Pro, ChatGPT 4.1, Claude 3.7, and DeepSeek R1, plus a recommended setup, legacy, and testing options depending on language and availability.',
    answer: (
      <>
        <p>
          Our active translation lineup currently includes ChatGPT-5, Gemini 2.5 Pro, ChatGPT 4.1, Claude 3.7, and DeepSeek R1.
        </p>
        <p>
          You may also see a Recommended setup option, along with legacy or testing models depending on language pair and account availability.
        </p>
      </>
    ),
  },
  { key: '22', category: 'languages', question: 'How Is ScribeShadow Different Than Chat GPT?', plainText: 'Faster, tailored instructions, and organized workflows for books and translations.', answer: (<p>ScribeShadow is faster, uses tailored instructions, and organizes your books and translations in one place.</p>) },
  { key: '23', category: 'rights', question: 'Do I Need to Credit ScribeShadow?', plainText: 'Attribution is optional. Link to the app if desired.', answer: (<p>No, attribution is optional. If desired, credit ScribeShadow with a link to <a href="https://app.scribeshadow.com">https://app.scribeshadow.com</a>.</p>) },
  { key: '24', category: 'rights', question: 'Do You Keep Deleted Books?', plainText: 'No. Deleted content is permanently removed.', answer: (<p>No. Deleted books and translations are permanently removed.</p>) },
];

/** Helpers */
const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlight = (text: string, q: string): React.ReactNode => {
  if (!q) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, 'ig'));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase() ? <mark key={i}>{p}</mark> : <span key={i}>{p}</span>
      )}
    </>
  );
};

const About: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [faqQuery, setFaqQuery] = useState<string>('');
  const [activeFAQCategory, setActiveFAQCategory] = useState<FAQCategoryKey>(() =>
    parseTopicParam(searchParams.get('topic'))
  );
  const [openFAQKeys, setOpenFAQKeys] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>(() =>
    parseTabParam(searchParams.get('tab'))
  );
  const [wordsTranslated, setWordsTranslated] = useState<number | null>(() =>
    getCachedWordsTranslated()
  );
  const [wordsCounterError, setWordsCounterError] = useState(false);
  const [displayedWordsTranslated, setDisplayedWordsTranslated] = useState<number>(() => {
    const cached = getCachedWordsTranslated();
    return cached ?? 0;
  });
  const wordsTickerBaseRef = useRef<number | null>(getCachedWordsTranslated());
  const wordsPerSecondRef = useRef<number>(DEFAULT_WORDS_PER_SECOND);

  useEffect(() => {
    let cancelled = false;
    fetchWordsTranslated()
      .then((value) => {
        if (cancelled) return;
        setWordsTranslated(value);
        setWordsCounterError(false);
        wordsTickerBaseRef.current = Math.max(value, wordsTickerBaseRef.current ?? 0);
        wordsPerSecondRef.current = Math.max(DEFAULT_WORDS_PER_SECOND, MIN_WORDS_PER_SECOND);
        setDisplayedWordsTranslated((prev) => {
          if (!Number.isFinite(prev) || prev < value) {
            return value;
          }
          return prev;
        });
      })
      .catch((error) => {
        console.error('Failed to load words translated counter', error);
        if (cancelled) return;
        setWordsCounterError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const switchToTab = (key: TabKey, anchorId?: string): void => {
    setActiveTab(key);
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      if (key === 'faq') {
        nextParams.delete('tab');
      } else {
        nextParams.set('tab', key);
      }
      if (key !== 'faq') {
        nextParams.delete('topic');
      } else if (activeFAQCategory !== 'all') {
        nextParams.set('topic', activeFAQCategory);
      } else {
        nextParams.delete('topic');
      }
      return nextParams;
    });
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        if (anchorId) {
          const el = document.getElementById(anchorId);
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  };

  useEffect(() => {
    const nextTab = parseTabParam(searchParams.get('tab'));
    const nextTopic = parseTopicParam(searchParams.get('topic'));
    setActiveTab((currentTab) => currentTab === nextTab ? currentTab : nextTab);
    setActiveFAQCategory((currentTopic) => currentTopic === nextTopic ? currentTopic : nextTopic);
  }, [searchParams]);

  const filteredFAQs = useMemo(() => {
    const q = faqQuery.trim().toLowerCase();
    return faqItems.filter(
      (f) => (
        activeFAQCategory === 'all' || f.category === activeFAQCategory
      ) && (
        !q || f.question.toLowerCase().includes(q) || f.plainText.toLowerCase().includes(q)
      )
    );
  }, [activeFAQCategory, faqQuery]);

  const collapseItems = useMemo<CollapseProps['items']>(() => (
    filteredFAQs.map((f) => ({
      key: f.key,
      label: <span>{highlight(f.question, faqQuery)}</span>,
      children: f.answer,
      style: panelStyle,
    }))
  ), [filteredFAQs, faqQuery]);

  useEffect(() => {
    const visibleKeys = new Set(filteredFAQs.map((faq) => faq.key));
    setOpenFAQKeys((currentKeys) => currentKeys.filter((key) => visibleKeys.has(key)));
  }, [filteredFAQs]);

  const wordsTranslatedDisplay = useMemo(() => {
    if (wordsCounterError) {
      return 'Check back soon';
    }
    const showLoading = wordsTranslated === null && displayedWordsTranslated <= 0;
    if (showLoading) {
      return 'Loading...';
    }
    const value = wordsTranslated !== null ? displayedWordsTranslated : displayedWordsTranslated || 0;
    return new Intl.NumberFormat('en-US').format(Math.max(0, Math.floor(value)));
  }, [displayedWordsTranslated, wordsCounterError, wordsTranslated]);

  const wordsCounterReady = !wordsCounterError && wordsTranslated !== null;

  useEffect(() => {
    if (wordsCounterError) return;
    const base = wordsTickerBaseRef.current;
    if (!Number.isFinite(base) || !base || base <= 0) {
      return;
    }
    wordsPerSecondRef.current = Math.max(DEFAULT_WORDS_PER_SECOND, MIN_WORDS_PER_SECOND);
    let lastTick = Date.now();
    const tickInterval = window.setInterval(() => {
      const now = Date.now();
      const elapsedSeconds = Math.max((now - lastTick) / 1000, 0);
      lastTick = now;
      setDisplayedWordsTranslated((prev) => {
        const baseline = wordsTickerBaseRef.current ?? base;
        if (!Number.isFinite(baseline) || baseline <= 0) {
          return prev;
        }
        const previousValue = Number.isFinite(prev) && prev > 0 ? prev : baseline;
        const next = previousValue + Math.max(wordsPerSecondRef.current, MIN_WORDS_PER_SECOND) * elapsedSeconds;
        if (!Number.isFinite(next)) {
          return baseline;
        }
        return Math.max(next, baseline);
      });
    }, 1000);
    return () => window.clearInterval(tickInterval);
  }, [wordsCounterError, wordsTranslated]);

  const tabs: AboutTab[] = [
    {
      key: 'faq',
      label: 'FAQ',
      icon: <QuestionCircleOutlined />,
    },
    {
      key: 'updates',
      label: 'Updates',
      icon: <HistoryOutlined />,
    },
    {
      key: 'contact',
      label: 'Contact',
      icon: <ContactsOutlined />,
    },
  ];

  const handleFAQCategoryChange = (category: FAQCategoryKey) => {
    setActiveFAQCategory(category);
    setOpenFAQKeys([]);
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.delete('tab');
      if (category === 'all') {
        nextParams.delete('topic');
      } else {
        nextParams.set('topic', category);
      }
      return nextParams;
    });
  };

  const handleFAQCollapseChange = (keys: string | string[]) => {
    setOpenFAQKeys(Array.isArray(keys) ? keys.map(String) : [String(keys)]);
  };

  const expandAllFAQs = () => {
    setOpenFAQKeys(filteredFAQs.map((faq) => faq.key));
  };

  const collapseAllFAQs = () => {
    setOpenFAQKeys([]);
  };

  return (
    <main className="about-page-shell">
      <div className="about-page-inner">
        <AppPageHeader
          className="about-hero"
          variant="banner"
          title="About"
          titleId="about-title"
          subtitle="ScribeShadow helps authors translate, proofread, and prepare books for international readers using AI-assisted publishing tools."
          actions={(
            <div className="about-banner-contact" aria-label="Contact ScribeShadow">
              <span>Contact</span>
              <a href="mailto:support@scribeshadow.com" aria-label="Email ScribeShadow">Email</a>
              <a href="https://discord.com/invite/k7vnT38vPq" target="_blank" rel="noopener noreferrer" aria-label="Open Discord">Discord</a>
              <a href="https://www.facebook.com/groups/216351367578868/" target="_blank" rel="noopener noreferrer" aria-label="Open Facebook group">Facebook</a>
            </div>
          )}
        />

        <div className="about-workspace">
          <nav className="about-tabbar-shell" aria-label="About page sections">
            <div className="about-tabbar-main">
              <span className="about-tabbar-label">Sections</span>
              <div className="about-tabbar" role="tablist" aria-label="About sections">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      className={isActive ? 'about-tab about-tab--active' : 'about-tab'}
                      onClick={() => switchToTab(tab.key)}
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`about-tabpanel-${tab.key}`}
                      id={`about-tab-${tab.key}`}
                    >
                      <span className="about-tab-icon" aria-hidden="true">{tab.icon}</span>
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="about-sections-counter">
              <div className="about-counter-stack">
                <div className="about-saga-anchor" aria-hidden="true">
                  <SagaRaven
                    position="inline"
                    size="lg"
                    idleMixer
                    idleBase="idle_base"
                    idleVariants={['head_tilt','idle_2','idle_3','idle_4','idle_5','idle_6','idle_7','idle_8']}
                    priority={SAGA_SINGLETON_PRIORITIES.pageAccent}
                    mirror
                    style={{ pointerEvents: 'none' }}
                  />
                </div>
                <div className="about-counter-pill" aria-live="polite">
                  <span>Words translated by ScribeShadow</span>
                  <strong>
                    {wordsTranslatedDisplay}
                    {wordsCounterReady && wordsTranslatedDisplay !== 'Loading...' && wordsTranslatedDisplay !== 'Check back soon'
                      ? '+'
                      : ''}
                  </strong>
                </div>
              </div>
            </div>
          </nav>

          <div
            role="tabpanel"
            id={`about-tabpanel-${activeTab}`}
            aria-labelledby={`about-tab-${activeTab}`}
          >
            {activeTab === 'faq' ? (
              <section className="about-section-card" id="faq" aria-labelledby="about-faq-title">
                <div className="about-section-heading">
                  <div>
                    <span className="about-kicker">FAQ</span>
                    <h2 id="about-faq-title">Frequently asked questions</h2>
                    <p>Search common policy, translation, credit, formatting, and privacy answers.</p>
                  </div>
                  <div className="about-faq-tools" role="search">
                    <Input.Search
                      allowClear
                      placeholder="Search FAQs by keyword"
                      onChange={(e) => setFaqQuery(e.target.value)}
                      value={faqQuery}
                      aria-label="Search FAQs"
                    />
                  </div>
                </div>

                <div className="about-faq-toolbar">
                  <div className="about-category-filter-list" aria-label="Filter FAQ topics">
                    {faqCategoryFilters.map((filter) => (
                      <button
                        key={filter.key}
                        type="button"
                        className={
                          filter.key === activeFAQCategory
                            ? 'about-category-filter-chip about-category-filter-chip--active'
                            : 'about-category-filter-chip'
                        }
                        onClick={() => handleFAQCategoryChange(filter.key)}
                        aria-pressed={filter.key === activeFAQCategory}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                  <div className="about-faq-open-controls" aria-label="FAQ panel controls">
                    <button type="button" onClick={expandAllFAQs} disabled={filteredFAQs.length === 0}>
                      Expand all
                    </button>
                    <button type="button" onClick={collapseAllFAQs} disabled={openFAQKeys.length === 0}>
                      Collapse all
                    </button>
                  </div>
                </div>

                <Collapse
                  className="about-faq-collapse"
                  bordered={false}
                  activeKey={openFAQKeys}
                  onChange={handleFAQCollapseChange}
                  expandIconPosition="end"
                  items={collapseItems}
                />

                {filteredFAQs.length === 0 && (
                  <div className="about-empty-state">
                    <span>Nothing matched your search. Try shorter terms or synonyms.</span>
                    <span className="about-empty-actions">
                      <Link to="/support">Open Support</Link>
                      <a href="mailto:contact@scribeshadow.com">Email ScribeShadow</a>
                    </span>
                  </div>
                )}
              </section>
            ) : activeTab === 'updates' ? (
              <section className="about-section-card about-section-card--updates" id="updates" aria-labelledby="about-updates-title">
                <Updates variant="embedded" panelStyle={panelStyle} />
              </section>
            ) : (
              <section className="about-section-card" id="contact" aria-labelledby="about-contact-title">
                <div className="about-section-heading">
                  <div>
                    <span className="about-kicker">Contact</span>
                    <h2 id="about-contact-title">Contact ScribeShadow</h2>
                    <p>Choose the channel that fits your question.</p>
                  </div>
                </div>
                <div className="about-contact-grid">
                  <a href="mailto:support@scribeshadow.com">Email support</a>
                  <a href="https://discord.com/invite/k7vnT38vPq" target="_blank" rel="noopener noreferrer">Open Discord</a>
                  <a href="https://www.facebook.com/groups/216351367578868/" target="_blank" rel="noopener noreferrer">Visit Facebook group</a>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default About;
